import * as React from 'react';
import { useEffect, useCallback } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';
import { toObserver, ActorStatus, createActor } from 'xstate';
import useIsomorphicLayoutEffect from 'use-isomorphic-layout-effect';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';

function useConstant(fn) {
  const ref = React.useRef();
  if (!ref.current) {
    ref.current = {
      v: fn()
    };
  }
  return ref.current.v;
}

function useIdleInterpreter(machine, options) {
  const actorRef = useConstant(() => {
    return createActor(machine, options);
  });

  // TODO: consider using `useAsapEffect` that would do this in `useInsertionEffect` is that's available
  useIsomorphicLayoutEffect(() => {
    actorRef.logic.implementations = machine.implementations;
  });
  return actorRef;
}
function useActorRef(machine, ...[options = {}, observerOrListener]) {
  const actorRef = useIdleInterpreter(machine, options);
  useEffect(() => {
    if (!observerOrListener) {
      return;
    }
    let sub = actorRef.subscribe(toObserver(observerOrListener));
    return () => {
      sub.unsubscribe();
    };
  }, [observerOrListener]);
  useEffect(() => {
    actorRef.start();
    return () => {
      actorRef.stop();
      actorRef.status = ActorStatus.NotStarted;
      actorRef._initState();
    };
  }, []);
  return actorRef;
}

function useActor(logic, options = {}) {
  const actorRef = useIdleInterpreter(logic, options);
  const getSnapshot = useCallback(() => {
    return actorRef.getSnapshot();
  }, [actorRef]);
  const subscribe = useCallback(handleStoreChange => {
    const {
      unsubscribe
    } = actorRef.subscribe(handleStoreChange);
    return unsubscribe;
  }, [actorRef]);
  const actorSnapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => {
    actorRef.start();
    return () => {
      actorRef.stop();
      actorRef.status = ActorStatus.NotStarted;
      actorRef._initState();
    };
  }, [actorRef]);
  return [actorSnapshot, actorRef.send, actorRef];
}

function defaultCompare(a, b) {
  return a === b;
}
function useSelector(actor, selector, compare = defaultCompare) {
  const subscribe = useCallback(handleStoreChange => {
    const {
      unsubscribe
    } = actor.subscribe(handleStoreChange);
    return unsubscribe;
  }, [actor]);
  const boundGetSnapshot = useCallback(() => actor.getSnapshot(), [actor]);
  const selectedSnapshot = useSyncExternalStoreWithSelector(subscribe, boundGetSnapshot, boundGetSnapshot, selector, compare);
  return selectedSnapshot;
}

// From https://github.com/reduxjs/react-redux/blob/720f0ba79236cdc3e1115f4ef9a7760a21784b48/src/utils/shallowEqual.ts
function is(x, y) {
  if (x === y) {
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  } else {
    return x !== x && y !== y;
  }
}
function shallowEqual(objA, objB) {
  if (is(objA, objB)) return true;
  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false;
  }
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) return false;
  for (let i = 0; i < keysA.length; i++) {
    if (!Object.prototype.hasOwnProperty.call(objB, keysA[i]) || !is(objA[keysA[i]], objB[keysA[i]])) {
      return false;
    }
  }
  return true;
}

function createActorContext(actorLogic, interpreterOptions) {
  const ReactContext = /*#__PURE__*/React.createContext(null);
  const OriginalProvider = ReactContext.Provider;
  function Provider({
    children,
    logic: providedLogic = actorLogic,
    machine,
    options: providedOptions = interpreterOptions
  }) {
    if (machine) {
      throw new Error(`The "machine" prop has been deprecated. Please use "logic" instead.`);
    }
    const actor = useActorRef(providedLogic, providedOptions);
    return /*#__PURE__*/React.createElement(OriginalProvider, {
      value: actor,
      children
    });
  }

  // TODO: add properties to actor ref to make more descriptive
  Provider.displayName = `ActorProvider`;
  function useContext() {
    const actor = React.useContext(ReactContext);
    if (!actor) {
      throw new Error(`You used a hook from "${Provider.displayName}" but it's not inside a <${Provider.displayName}> component.`);
    }
    return actor;
  }
  function useSelector$1(selector, compare) {
    const actor = useContext();
    return useSelector(actor, selector, compare);
  }
  return {
    Provider: Provider,
    useActorRef: useContext,
    useSelector: useSelector$1
  };
}

/**
 * @alias useActor
 */
function useMachine(machine, options = {}) {
  return useActor(machine, options);
}

export { createActorContext, shallowEqual, useActor, useActorRef, useMachine, useSelector };
